import { ApiError, verifyToken } from "./http.js";
import { getAuth, rememberTournament, setAuth } from "./storage.js";
import { navigate } from "./router.js";
import { TERMS } from "./copy.js";
import { VALID_TOKEN } from "./organiserLink.js";

/**
 * Putting an organiser back into a game, from a link they followed or one they pasted.
 *
 * One function for both doors on purpose, and not just to stop them drifting: the
 * pasted-link screen must never restore access by sending the browser to
 * `#/t/<code>/organizer/<token>`, which is the obvious shortcut. That URL would go into
 * history — and, on a phone, into restored session state — where a permanent,
 * unrevocable credential has no business being. The paste screen calls this directly and
 * the token never reaches the address bar.
 */
export async function restoreOrganiserAccess(rawCode: string, rawToken: string): Promise<void> {
  // Uppercased here rather than at each call site. Storage keys are built from the code
  // (`golf:auth:<CODE>`), and nothing on the routing side uppercases a code that came
  // out of a path — so a hand-lowercased link used to quietly create a second, parallel
  // entry that shared nothing with the real one.
  const code = rawCode.trim().toUpperCase();
  const token = rawToken.trim();

  if (!VALID_TOKEN.test(token)) {
    throw new Error("that link looks cut short");
  }

  let verified: { role: string; name: string };
  try {
    verified = await verifyToken(code, token);
  } catch (err) {
    throw new Error(explain(err, code));
  }

  if (verified.role !== "admin") {
    throw new Error(
      `that's an invite link, not an ${TERMS.organizer} one — it opens ${verified.name} as a player`,
    );
  }

  // Keep the seat if this device is re-restoring the same token. The next state frame
  // would fill it back in anyway (`syncOrganizerSeat`), but until it lands the app bar
  // would have dropped the switcher — an organiser who is also playing would watch
  // their own identity blink out for no reason they could see.
  const prior = getAuth(code);
  const playerId = prior?.token === token ? prior.playerId : null;

  setAuth(code, { token, role: "admin", playerId });
  rememberTournament({ code, name: verified.name, role: "admin" });
  navigate(`/t/${code}/setup`);
}

/** Turns a failed check into something worth reading. The generic "Failed to fetch" a
 * dead connection produces is the one an organiser is most likely to hit, standing on a
 * course with no signal, and it says nothing at all. */
function explain(err: unknown, code: string): string {
  if (err instanceof ApiError) {
    if (err.status === 404) {
      return `there's no ${TERMS.event} ${code} any more — it may have been deleted, or expired`;
    }
    if (err.status === 401) return `that link doesn't open ${code}, so it may have been cut short`;
    if (err.status === 429) return "too many tries just now — give it a minute";
    return err.message;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "you're offline — this one needs a connection";
  }
  return (err as Error).message;
}
